import { BRAND_CEL, BRAND_RCI, HOME_PAGE_QUERY_PARAM } from 'contexts/CruiseSearchContext/constants';
import { EMPTY_CRUISE_RECOMMENDATION_ID } from 'contexts/PersonalizationContext/constants';
import { getFormatFill, getFormatPromoDashboard, getNumberGuest, getSearchMonths, getTotalDays } from 'lib/ui-analytics/utils';
import { TRACKING_INTERACTION } from './Constants';

export const propertyNames = {
	[BRAND_RCI.code]: {
		currency: 'currency',
		type: 'cruiseSearchType',
		destination: 'cruiseSearchDestCodes',
		destinationPorts: 'cruiseSearchShipPorts',
		months: 'cruiseSearchMonths',
		ports: 'cruiseSearchPorts',
		nights: 'cruiseSearchNights',
		guestsAdults: 'cruiseSearchNumberOfGuests',
		guestsChildren: 'cruiseSearchGuestsChildren',
		ships: 'cruiseSearchShipCodes',
		results: 'cruiseSearchResults',
		event: 'productEvent',
		departureDate: 'csDepartureDate',
		productSku: 'prodSku',
		cardPosition: 'cardPosition',
		country: 'country',
		csDepartureDate: 'departureDate',
		siteSection: 'siteSection',
		pageName: 'pageName',
		productEvent: 'productEvent',
	},
	[BRAND_CEL.code]: {
		currency: 'currency_code',
		type: 'Search_Type',
		destination: 'search_destination',
		destinationPorts: 'search_destinations_ports',
		months: 'search_departure_month',
		ports: 'search_departure_port',
		nights: 'search_num_nights',
		guestsAdults: 'cruiseSearchNumberOfGuests',
		guestsChildren: 'cruiseSearchGuestsChildren',
		ships: 'search_cruise_ship_codes',
		results: 'search_results',
		event: 'product_event',
		departureDate: 'sailing_date',
		productSku: 'product_sku',
		cardPosition: 'card_position',
		country: 'CountryOfResidence',
		csDepartureDate: 'departureDate',
		siteSection: 'site_section',
		pageName: 'page_name',
		productEvent: 'product_event',
	},
};

export const createUserInteraction = (event, extra = {}) => {
	const { eventType, interaction, changeNameInteraction = false, addEventType = true } = event;
	const interactionName = changeNameInteraction ? eventType : Object.keys(event)?.[1];
	const interactionValue = interaction || event[interactionName];

	const extraParams = extra ? { ...extra } : {};
	let interactionToSend = {
		[interactionName]: interactionValue,
		...extraParams,
	};

	if (addEventType) {
		interactionToSend.eventType = eventType;
	}

	return interactionToSend;
};

export const createInitialPageTrack = (
	search,
	locale,
	results,
	pagination,
	perks,
	brand = BRAND_RCI.code,
	sort = [],
	user = {},
	isAuthenticated = false,
	cruiseRecommendationId = EMPTY_CRUISE_RECOMMENDATION_ID,
	query = {},
) => {
	const searchMonths = getSearchMonths(search);
	const promotionPerks = getFormatPromoDashboard(perks);

	const formatProductSkuValue = (brand, value) => (brand === BRAND_CEL.code ? `Booking-${value}` : value);

	let tracking = {
		country: locale.country,
		language: locale.language,
		[propertyNames[brand].currency]: locale.currency,
		[propertyNames[brand].destination]: search?.destination ? search.destination : 'Any',
		[propertyNames[brand].destinationPorts]: search?.port ? search.port : 'Any',
		[propertyNames[brand].months]: searchMonths,
		[propertyNames[brand].ports]: search?.departurePort ? search.departurePort : 'Any',
		[propertyNames[brand].nights]: search?.nights ? [`${search.nights[0].replace('~', '-')} Nights`] : 'Any',
		[propertyNames[brand].guestsAdults]: getNumberGuest(search?.adults, search?.children),
		[propertyNames[brand].guestsChildren]: search?.children ? search.children[0] : 'Any',
		[propertyNames[brand].ships]: search?.ship ? search.ship : 'Any',
		[propertyNames[brand].results]: results?.total ? results.total : 0,
		pagination: pagination && pagination.count ? `page ${pagination.currentPage + 1} of ${Math.ceil(results?.total / pagination.count)}` : '',
		prodCategory: results?.cruises?.length ? getFormatFill(results.cruises.length, 'booking') : '',
		[propertyNames[brand].event]: results?.cruises?.length ? getFormatFill(results.cruises.length, 'impression') : '',
		[propertyNames[brand].productSku]: results?.cruises?.length
			? results.cruises.map((cruise) => formatProductSkuValue(brand, cruise.masterSailing?.itinerary?.code))
			: '',
		prodCost: results?.cruises?.length
			? results.cruises.map((cruise) => Math.round(cruise.lowestPriceSailing?.lowestStateroomClassPrice?.price?.value))
			: '',
		[propertyNames[brand].cardPosition]: results?.cruises?.length ? results.cruises.map((_, i) => i + 1) : '',
		[propertyNames[brand].departureDate]: results?.cruises?.length ? results.cruises.map((cruise) => cruise.lowestPriceSailing?.sailDate) : '',
		groupId: results?.cruises?.length ? results.cruises.map((cruise) => cruise.lowestPriceSailing?.id) : '',
		prodQuantity: results?.cruises?.length ? getFormatFill(results.cruises.length, 1) : '',
	};

	if (cruiseRecommendationId) {
		tracking = {
			...tracking,
			cruiseRecommendationId,
		};
	}

	if (brand === BRAND_RCI.code) {
		tracking = {
			...tracking,
			cruiseSearchCurrency: locale.currency,
			promoDashboard: promotionPerks.length ? promotionPerks.join(',') : '',
		};
	}

	if (brand === BRAND_CEL.code) {
		let endMonth = 'Any';
		let startMonth = 'Any';

		if (search.startDate) {
			startMonth = search.startDate[0]?.split('~')[0] || 'Any';
			endMonth = search.startDate[search?.startDate?.length - 1]?.split('~')[1] || 'Any';
		}

		tracking = {
			...tracking,
			[propertyNames[brand].type]: Object.keys(search).length
				? TRACKING_INTERACTION.SEARCH_ORIGIN.FILTER_UPDATE
				: TRACKING_INTERACTION.SEARCH_ORIGIN.NL_SEARCH,
			consumer_id: '',
			customer_ID: 0,
			captains_club_id: 0,
			Booking_Search_Details: 'Popular Cruise | Position',
			booking_options: 'none | none | none | 0000000 | none | none',
			cruise_dateToggle: 'M',
			cruise_package: 'cruisetours',
			link_name: window?.location?.href || '',
			logged_in: isAuthenticated ? 1 : 0,
			new_search_results: 1,
			page_path: '/cruises',
			page_section_type: 'cruise-search',
			search_cruise_package: 'CO,CT',
			search_departure_month: startMonth,
			search_departure_end_month: endMonth,
			search_refine_accessibility: 'none',
			search_refine_phrase: 'none',
			search_refine_room_types: 'CO,CT | none',
			search_refine_total_days: search?.nights ? getTotalDays(search.nights) : 'Any',
			search_refine_total_guests: 'none',
			search_refine_total_refinements_qty: Object.keys(search).length,
			search_sort_by: sort?.join(','),
			VDSID: user?.vdsId || 0,
			search_refine_ships: search?.ship ? search.ship : 'Any',
		};
	}

	tracking = {
		...tracking,
		...(query[HOME_PAGE_QUERY_PARAM]
			? { [propertyNames[brand].type]: `${TRACKING_INTERACTION.SEARCH_ORIGIN.SEARCH_WIDGET_INIT}${query[HOME_PAGE_QUERY_PARAM]}` }
			: {}),
	};

	return tracking;
};

export const getInteraction = (searchIndex, resultsLength, masterSailing, action) => {
	return `cruise search : ${searchIndex} of ${resultsLength} : ${masterSailing?.itinerary?.totalNights} night ${masterSailing?.itinerary?.name}: ${action}`;
};

export const getUrgencyParam = (urgencyText) => {
	if (!urgencyText) return 'no urgency text';
	if (urgencyText.includes('view')) return 'people viewing';
	if (urgencyText.includes('watch')) return 'people watching';
	if (urgencyText.includes('book')) return 'people booking';
};
